/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import type { OnboardingEditorState } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useSessionData } from '@confluence/session-data';
import { getIsProductOrSiteAdminFromQuery } from '@confluence/current-user';

import type {
	useIncludeEditorTutorialTemplateGetUserContentQuery as useIncludeEditorTutorialTemplateGetUserContentQuery$data,
	useIncludeEditorTutorialTemplateGetUserContentQueryVariables as useIncludeEditorTutorialTemplateGetUserContentQuery$variables,
} from './__types__/useIncludeEditorTutorialTemplateGetUserContentQuery';
import type { useIncludeEditorTutorialTemplateGetUserPrivilegesQuery as useIncludeEditorTutorialTemplateGetUserPrivilegesQuery$data } from './__types__/useIncludeEditorTutorialTemplateGetUserPrivilegesQuery';
export const ADMIN_THRESHOLD = 11;
export const NTH_USER_THRESHOLD = 7;

export const useIncludeEditorTutorialTemplate = () => {
	const { isLicensed, userId, isLoggedIn } = useSessionData();

	const {
		data: editorOnboardingData,
		loading: editorOnboardingLoading,
		error: editorOnboardingError,
	} = useGetOnboardingState(Object.values(EDITOR_ONBOARDING_STATE_KEYS));

	const {
		data: userContentData,
		loading: userContentLoading,
		error: userContentError,
	} = useQuery<
		useIncludeEditorTutorialTemplateGetUserContentQuery$data,
		useIncludeEditorTutorialTemplateGetUserContentQuery$variables
	>(
		gql`
			query useIncludeEditorTutorialTemplateGetUserContentQuery($cql: String!) {
				search(cql: $cql, first: 12) {
					count
				}
				isSiteAdmin
			}
		`,
		{
			variables: {
				cql: `type = 'page' AND creator = '${userId}'`,
			},
			skip: !isLicensed || !isLoggedIn,
		},
	);

	const {
		data: privilegeData,
		loading: privilegeLoading,
		error: privilegeError,
	} = useQuery<useIncludeEditorTutorialTemplateGetUserPrivilegesQuery$data>(gql`
		query useIncludeEditorTutorialTemplateGetUserPrivilegesQuery {
			user(current: true) {
				id
				confluence {
					operations {
						operation
						targetType
					}
				}
			}
			isSiteAdmin
			hasUserAccessAdminRole
		}
	`);

	// Onboarding related variables
	const deserializedEditorOnboardingState: OnboardingEditorState =
		deserializeState(editorOnboardingData);

	const { editorOnboardingEligiblity } = deserializedEditorOnboardingState;
	const queriesLoadingOrErrored: boolean =
		editorOnboardingLoading ||
		Boolean(editorOnboardingError) ||
		userContentLoading ||
		Boolean(userContentError) ||
		privilegeLoading ||
		Boolean(privilegeError);
	const isProductOrSiteAdmin = getIsProductOrSiteAdminFromQuery(privilegeData);

	const hasQueryErrors: boolean =
		Boolean(editorOnboardingError) || Boolean(userContentError) || Boolean(privilegeError);

	// Admins get automatically created 9 pages, while Nth users only get created 5.
	const publishedPagesThreshold = isProductOrSiteAdmin ? ADMIN_THRESHOLD : NTH_USER_THRESHOLD;

	// If there is no data on how many pages they published, then assume that the template shouldn't be visible.
	const numberOfPagesPublished = userContentData?.search?.count ?? publishedPagesThreshold;

	const shouldIncludeEditorTutorialTemplate: boolean =
		!queriesLoadingOrErrored && // Show the template if onboarding state is not loading / has an error
		Boolean(editorOnboardingEligiblity) && // Has to be onboarding eligible (new user)
		numberOfPagesPublished < publishedPagesThreshold; // Don't show if they have published 2 pages (excluding the auto-generated pages)

	return {
		shouldIncludeEditorTutorialTemplate,
		userContentError,
		editorOnboardingError,
		privilegeError,
		isProductOrSiteAdmin,
		queriesLoadingOrErrored,
		hasQueryErrors,
	};
};
